import {
  TriageModel,
  TriageNoteModel,
  TriageRecipientRole,
} from 'data/api/triage.api';

/**
 * When the triage was raised, in milliseconds. The column is
 * `triages_created_date`; `createdDate` is only read as a fallback so the
 * inline thread ordering cannot collapse on a field-name difference.
 */
export const triageCreatedAt = (triage: TriageModel): number => {
  const raw = triage.triagesCreatedDate || triage.createdDate;
  const at = raw ? new Date(raw).valueOf() : NaN;
  return Number.isFinite(at) ? at : 0;
};

export const fullName = (person: {
  firstName?: string;
  lastName?: string;
  email?: string;
}): string =>
  [person.firstName, person.lastName].filter(Boolean).join(' ') ||
  person.email ||
  '';

export const recipientNames = (
  triage: TriageModel,
  role: TriageRecipientRole,
): string[] =>
  (triage.recipients || [])
    .filter((recipient) => recipient.role === role)
    .map(fullName)
    .filter(Boolean);

export const noteAuthorName = (note: TriageNoteModel): string =>
  note.createdByUser ? fullName(note.createdByUser) : '';

const ENTITIES: { [key: string]: string } = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

// Only an image the server will send counts as content. A remote one is
// dropped from the email, so a message holding nothing else is empty.
const INLINE_IMAGE = /<img\s[^>]*src="data:image\/(png|jpeg|gif|webp);base64,/i;

/** Whether a composed message has anything to send: text, or a pasted image. */
export const hasMessageContent = (html?: string): boolean =>
  !!plainText(html) || INLINE_IMAGE.test(html || '');

/**
 * Note bodies are editor HTML. This is for snippets and for the inline thread
 * card only — anywhere the body itself is shown it goes through
 * DOMPurify + html-react-parser like every other note in the admin.
 */
export const plainText = (html?: string): string =>
  (html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;/g, (match) => ENTITIES[match])
    .replace(/\s+/g, ' ')
    .trim();

/**
 * The note whose email status the card reports. Notes arrive ascending, so
 * this is the most recent attempt to reach the recipients — an older failure
 * that has since been followed by a delivered reply is not a live failure.
 */
export const latestOutboundNote = (
  triage: TriageModel,
): TriageNoteModel | undefined =>
  [...(triage.notes || [])]
    .reverse()
    .find((note) => note.direction === 'outbound');

// The server's refusals for a message's images (BL-TASK-1423). The API layer
// hands back only the message text, upper-cased, so this matches on it and
// returns the readable wording. Fastify's own 413 means the images were
// larger than the route accepts at all.
const IMAGE_REFUSALS = [
  'Images must be PNG, JPEG, GIF or WebP pasted into the message',
  'Each image must be 1 MB or smaller',
  'Images in one message must total 3 MB or less',
  'The message text is too long',
];

export const imageRefusal = (error: unknown): string | null => {
  const text = (
    typeof error === 'string' ? error : (error as Error)?.message || ''
  ).toUpperCase();
  if (text === 'REQUEST BODY IS TOO LARGE') {
    return 'Images in one message must total 3 MB or less';
  }
  return (
    IMAGE_REFUSALS.find((message) => message.toUpperCase() === text) ?? null
  );
};
